/** Small display helpers shared across the admin conversation list and detail. */

import type {
  ChatMessage,
  ChatThreadDetail,
  ChatThreadPage,
  ChatThreadSummary,
} from "~/types/chat-admin";

export function chatMemberName(
  thread: Pick<ChatThreadSummary, "user_display_name" | "user_email">,
): string {
  return thread.user_display_name?.trim() || thread.user_email;
}

/**
 * Merge offset pages defensively. A new message can shift page boundaries between
 * polls, so the same message may temporarily occur on two loaded pages.
 */
export function uniqueChatMessages(
  pages: ReadonlyArray<Pick<ChatThreadDetail, "messages">>,
): ChatMessage[] {
  const byId = new Map<string, ChatMessage>();
  // React Query keeps offset zero first. Prefer that freshest copy when pages overlap.
  for (const page of pages) {
    for (const message of page.messages) {
      if (!byId.has(message.id)) byId.set(message.id, message);
    }
  }
  return [...byId.values()].sort(
    (left, right) =>
      left.created_at.localeCompare(right.created_at) ||
      left.id.localeCompare(right.id),
  );
}

/**
 * Merge offset inbox pages without letting a newly active thread create duplicate rows.
 * Page zero is fetched first and carries the freshest unread/message state, while the
 * explicit activity/id sort restores the backend's deterministic inbox order.
 */
export function uniqueChatThreads(
  pages: ReadonlyArray<Pick<ChatThreadPage, "items">>,
): ChatThreadSummary[] {
  const byId = new Map<string, ChatThreadSummary>();
  for (const page of pages) {
    for (const thread of page.items) {
      if (!byId.has(thread.id)) byId.set(thread.id, thread);
    }
  }
  return [...byId.values()].sort((left, right) => {
    const leftActivity = left.last_message?.created_at ?? left.updated_at;
    const rightActivity = right.last_message?.created_at ?? right.updated_at;
    return (
      rightActivity.localeCompare(leftActivity) ||
      right.id.localeCompare(left.id)
    );
  });
}
